import { Controller } from '@hotwired/stimulus';
import TomSelect from 'tom-select';

/**
 * Hierarchical Select Controller
 *
 * Enhances a <select> element with Tom Select to display options with
 * hierarchical sub-items rendered as indented bullet points beneath each option.
 * Sub-items are clickable — selecting one stores its ID in a target hidden input.
 *
 * Sub-item data is read from `data-sub-items` and `data-sub-item-ids` attributes
 * on <option> elements, with items delimited by "||".
 *
 * @example
 * <div data-controller="hierarchical-select">
 *   <select data-hierarchical-select-target="select">
 *     <option value="">Select...</option>
 *     <option value="1" data-sub-items="Detail A" data-sub-item-ids="A">Option 1</option>
 *     <option value="2" data-sub-items="Detail A||Detail B" data-sub-item-ids="A||B">Option 2</option>
 *     <option value="3">Option 3</option>
 *   </select>
 *   <input type="hidden" data-hierarchical-select-target="subItemId" />
 * </div>
 *
 * Renders dropdown options as:
 *   • Option 1
 *       ○ Detail A        (clickable)
 *   • Option 2
 *       ○ Detail A        (clickable)
 *       ○ Detail B        (clickable)
 *   • Option 3
 */
export default class extends Controller {
  static targets = ['select', 'subItemId'];

  connect() {
    this.selectedSubItemId = null;
    this.selectedSubItemLabel = null;
    this.initTomSelect();
  }

  disconnect() {
    if (this.tomSelectInstance) {
      this.tomSelectInstance.destroy();
      this.tomSelectInstance = null;
    }
  }

  initTomSelect() {
    const selectElement = this.hasSelectTarget
      ? this.selectTarget
      : this.element;

    this.tomSelectInstance = new TomSelect(selectElement, {
      maxItems: 1,
      render: {
        item: (data, escape) => {
          const name = escape(data.text || '');
          if (this.selectedSubItemLabel) {
            return `<div data-ts-item>${name} — ${escape(this.selectedSubItemLabel)}</div>`;
          }
          return `<div data-ts-item>${name}</div>`;
        },
        option: (data, escape) => {
          const name = escape(data.text || '');
          const subItems = data.subItems;
          const subItemIds = data.subItemIds;

          if (!subItems) {
            return `<div class="ts-hierarchical-option"><span class="ts-hierarchical-label">${name}</span></div>`;
          }

          const items = subItems.split('||');
          const ids = subItemIds ? subItemIds.split('||') : [];
          const subItemsHtml = items
            .map((item, index) => {
              const id = ids[index] || '';
              return `<div class="ts-hierarchical-sub-item ts-hierarchical-sub-item--selectable" data-sub-item-id="${escape(id)}" data-sub-item-label="${escape(item)}">${escape(item)}</div>`;
            })
            .join('');

          return `<div class="ts-hierarchical-option"><span class="ts-hierarchical-label">${name}</span>${subItemsHtml}</div>`;
        }
      },
      onDropdownOpen: () => {
        this.attachSubItemClickHandlers();
      },
      onChange: () => {
        this.clearSubItemSelection();
        this.updateItemDisplay();
      }
    });

    this.restoreSubItemSelection();
  }

  attachSubItemClickHandlers() {
    requestAnimationFrame(() => {
      const dropdown = this.tomSelectInstance?.dropdown_content;
      if (!dropdown) return;

      dropdown
        .querySelectorAll('.ts-hierarchical-sub-item--selectable')
        .forEach((selectable) => {
          selectable.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            const subItemId = selectable.dataset.subItemId;
            const subItemLabel = selectable.dataset.subItemLabel;
            const optionEl = selectable.closest('.ts-hierarchical-option');
            const optionValue = optionEl?.closest('[data-value]')?.dataset.value;

            this.selectedSubItemId = subItemId;
            this.selectedSubItemLabel = subItemLabel;

            if (optionValue) {
              this.tomSelectInstance.setValue(optionValue, true);
            }

            this.updateSubItemIdField();
            this.updateItemDisplay();
            this.tomSelectInstance.close();
          });
        });

      dropdown.querySelectorAll('.ts-hierarchical-label').forEach((label) => {
        label.addEventListener('click', () => {
          this.clearSubItemSelection();
        });
      });
    });
  }

  clearSubItemSelection() {
    this.selectedSubItemId = null;
    this.selectedSubItemLabel = null;
    this.updateSubItemIdField();
  }

  updateSubItemIdField() {
    if (this.hasSubItemIdTarget) {
      this.subItemIdTarget.value = this.selectedSubItemId || '';
    }
  }

  updateItemDisplay() {
    const controlInput = this.tomSelectInstance?.control;
    if (!controlInput) return;

    const itemEl = controlInput.querySelector('[data-ts-item]');
    if (!itemEl) return;

    const currentValue = this.tomSelectInstance.getValue();
    const option = this.tomSelectInstance.options[currentValue];
    if (!option) return;

    const name = option.text || '';
    if (this.selectedSubItemLabel) {
      itemEl.textContent = `${name} — ${this.selectedSubItemLabel}`;
    } else {
      itemEl.textContent = name;
    }
  }

  restoreSubItemSelection() {
    if (!this.hasSubItemIdTarget) return;

    const savedId = this.subItemIdTarget.value;
    if (!savedId) return;

    const currentValue = this.tomSelectInstance.getValue();
    if (!currentValue) return;

    const option = this.tomSelectInstance.options[currentValue];
    if (!option || !option.subItems || !option.subItemIds) return;

    const ids = option.subItemIds.split('||');
    const labels = option.subItems.split('||');
    const index = ids.indexOf(savedId);

    if (index !== -1) {
      this.selectedSubItemId = savedId;
      this.selectedSubItemLabel = labels[index];
      this.updateItemDisplay();
    }
  }
}
